---
id: alert-rule-generator
title: Alert Rule Generator
category: monitoring
difficulty: intermediate
tags:
  - alerting
  - prometheus
  - grafana
  - slo
  - monitoring
  - pagerduty
description: >
  Generates alert rules based on SLOs and service characteristics, with
  proper severity levels and runbook links.
whenToUse: >
  When setting up monitoring for a new service or reviewing existing
  alert rules for noise and coverage gaps.
variables:
  - name: service_name
    desc: Name of the service
  - name: service_type
    desc: Type of service (API, worker, database, cache, queue)
  - name: availability_slo
    desc: Availability target (e.g., 99.9%)
  - name: latency_slo
    desc: Latency target (e.g., p95 < 200ms)
  - name: error_budget
    desc: Monthly error budget percentage
  - name: platform
    desc: Infrastructure platform
  - name: monitoring_stack
    desc: Monitoring tools in use
tips:
  - Use multi-window, multi-burn-rate alerting for SLO-based alerts — avoids false positives.
  - Always include a for duration to prevent flappy alerts.
  - Define clear ownership — every alert must have a team label for routing.
---

You are an SRE designing alert rules. Generate production-ready alerts based on the service profile.

## Service Profile
- Name: {{service_name}}
- Type: {{service_type}} (API, worker, database, cache, queue)
- SLOs:
  - Availability: {{availability_slo}}
  - Latency P95: {{latency_slo}}
  - Error budget: {{error_budget}}

## Infrastructure
- Platform: {{platform}} (K8s, VMs, serverless)
- Monitoring stack: {{monitoring_stack}} (Prometheus/Grafana, Datadog, CloudWatch)

## Task
Generate alert rules that:
1. Cover the four golden signals (latency, traffic, errors, saturation)
2. Use multi-window burn rates for SLO-based alerting
3. Include proper severity levels (critical/warning/info)
4. Have meaningful alert names and descriptions
5. Avoid alert fatigue — no flappy alerts

For each alert provide:
- Rule definition in the target monitoring format
- Severity and routing (who gets paged)
- Runbook outline (first 3 diagnostic steps)

## Output Format
Provide alert rules in {{monitoring_stack}} native format with annotations.

<!-- EXAMPLE_OUTPUT -->

# payment-service SLO Alerts (Prometheus format)

groups:
  - name: payment-service.slo
    rules:
      - alert: PaymentServiceHighErrorRate
        expr: |
          sum(rate(http_requests_total{service="payment",code=~"5.."}[5m]))
          / sum(rate(http_requests_total{service="payment"}[5m]))
          > 0.001  # 10x burn rate on 99.9% SLO
        for: 5m
        labels:
          severity: critical
          team: payments
        annotations:
          summary: "Payment service error rate exceeding SLO burn rate"
          runbook: "https://wiki/runbooks/payment-high-errors"
